/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, OnDestroy, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {forkJoin, Subscription, timer} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {LoadingService} from '../../shared/loading/loading-service/loading.service';
import {ClaimDetailModel} from '../../api/ms-claims/contracts/claim-detail.model';
import {UserService} from '../../shared/services/user.service';
import {StickyHeaderIconButtonComponent} from '../../shared/sticky-header/html-elements/sticky-header-icon-button.component';
import {Permission} from '../../shared/utils/permission.enum';
import {StickyHeaderService} from '../../shared/sticky-header/sticky-header.service';
import {ConfirmModalComponent} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.component';
import {ConfirmModal} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.model';
import {AlertNotificationSeverity} from '../../alert-notification-severity.model';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ClaimsApi} from '../../api/ms-claims/claims.api';
import {ClaimPositionDetailModel} from '../../api/ms-claims/contracts/claim-position-detail.model';
import {ClaimInformationDataModel} from '../models/claim-information-data.model';
import {ClaimSendHelperService} from '../services/claim-send-helper.service';
import {AvailableActions} from '../../api/ms-claims/contracts/available-actions.enum';
import {ClaimStatusEnum} from '../../api/ms-claims/contracts/claim-status.enum';
import {HandlingUnitValidationService} from '../services/claim-detail/handling-unit-validation/handling-unit-validation.service';
import {createProgressFromStatusArray} from '../workflow-configuration/workflow-to-progress-model-mapper';
import {ClaimProgressStepModel} from '../shared/claim-progress/claim-progress-step.model';
import {ReferenceDataApi} from '../../api/ms-claims/reference-data.api';
import {finalize, take} from 'rxjs/operators';
import {ToastWrapperService} from '../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {FileManagerApi} from '../../api/ms-filemanager/filemanager.api';
import {FileSummaryModel} from '../../api/ms-filemanager/contracts/file-summary.model';
import {FolderDeleteService} from '../services/folder-delete.service';
import {SpinnerService} from '../../shared/spinner/spinner.service';
import {StatefulComponent} from '../../shared/stateful/stateful.component';

@Component({
    selector: 'app-claim-detail-page',
    templateUrl: './claim-detail-page.component.html',
    styleUrls: ['./claim-detail-page.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ClaimDetailPageComponent extends StatefulComponent<any> implements OnInit, OnDestroy {

    @ViewChild('tooltip', {static: true}) tooltip: TemplateRef<any>;
    public claimDetail: ClaimDetailModel;
    public attachments: FileSummaryModel[];
    public contextMenuOptions: StickyHeaderIconButtonComponent[];
    public Permission = Permission;
    public informationDetail: ClaimInformationDataModel;
    public positions: ClaimPositionDetailModel[] = [];
    AvailableActions = AvailableActions;
    public statusSchemaView: ClaimProgressStepModel = null;
    public fileManagerOffline: boolean;
    private subscription = new Subscription();

    public spinnerObservable = this.spinnerService.onGlobalChange();

    constructor(
            private activatedRoute: ActivatedRoute,
            private loadingService: LoadingService,
            private router: Router,
            public userService: UserService,
            private stickyHeaderService: StickyHeaderService,
            private modalService: NgbModal,
            private claimsApi: ClaimsApi,
            private toastrService: ToastWrapperService,
            private claimSendHelper: ClaimSendHelperService,
            private handlingUnitValidation: HandlingUnitValidationService,
            private referenceDataApi: ReferenceDataApi,
            private fileManagerApi: FileManagerApi,
            private folderDeleteService: FolderDeleteService,
            private toast: ToastWrapperService,
            private spinnerService: SpinnerService) {
        super();
    }

    ngOnInit(): void {
        this.fileManagerOffline = false;
        this.getClaimDetail();

        this.subscription.add(forkJoin(
                this.referenceDataApi.getStatusOptions(),
                this.activatedRoute.data.pipe(take(1))
        ).subscribe(([options, data]) => {
            this.statusSchemaView = createProgressFromStatusArray(options, data.claimDetail.statusSchema, this.tooltip);
        }));
        this.subscription.add(this.spinnerObservable.subscribe((isLoading) => {
            this.storeState({isLoading});
        }));

        setTimeout(() => {

            this.contextMenuOptions = [];

            if (this.userService.hasPermissions([Permission.PARTNER_CLAIM_EDIT])) {
                this.contextMenuOptions.push({
                    text: 'Edit',
                    icon: 'iwp-icon-gen_edit',
                    click: (): void => {
                        this.router.navigate(['/', 'claim', this.claimDetail.number, 'edit']);
                    },
                    disabled: !this.claimDetail.availableActions || this.claimDetail.availableActions
                            && !this.claimDetail.availableActions.includes(AvailableActions.EDIT),
                    id: 'sticky-header-edit-button',
                    class: 'btn-text-link',
                    dataCy: 'sticky-header-edit-button',
                    tooltip: 'Edit claim'
                });
            }

            if (this.userService.hasPermissions([Permission.PARTNER_CLAIM_DELETE])
                    && this.claimDetail.availableActions
                    && this.claimDetail.availableActions.includes(AvailableActions.DELETE)) {
                this.contextMenuOptions.push({
                    text: 'Delete',
                    icon: 'iwp-icon-gen_delete',
                    click: (): void => {
                        this.deleteClaim(this.claimDetail);
                    },
                    id: 'sticky-header-delete-button',
                    class: 'btn-text-link',
                    dataCy: 'sticky-header-delete-button',
                    tooltip: 'Delete Claim'
                });
            }

            if (this.userService.hasPermissions([Permission.PARTNER_CLAIM_CREATE])) {
                this.stickyHeaderService.registerMainAction({
                    text: 'New',
                    click: (): void => {
                        this.router.navigate(['/', 'claim', 'new']);
                    },
                    id: 'sticky-header-new-button',
                    class: 'btn-primary',
                    dataCy: 'sticky-header-new-button',
                    tooltip: 'Create new Claim'
                });
            }

            this.stickyHeaderService.registerContextActions(this.contextMenuOptions);

            this.updateSendButton();
        });

        /** Subscription to handle page loading **/
        this.subscription.add(
                this.claimSendHelper.enableLoading.subscribe(enableLoading => {
                    enableLoading ? this.loadingService.start() : this.loadingService.stop();
                })
        );
    }

    getClaimDetail(): void {
        this.subscription.add(this.activatedRoute.data.subscribe(data => {
            if (data.claimDetail != null) {
                this.claimDetail = data.claimDetail;
                this.informationDetail = this.mapClaimInformation(this.claimDetail);
                this.positions = this.claimDetail.claimPositions;
                // TODO: Remove timer()
                //  Creating dynamically a new view (ex.: toast) from a lifecycle hook (ex.: ngOnInit) will result in
                //  an 'ExpressionChangedAfterItHasBeenCheckedError'. Using timer() is an workaround.
                //  Issue on github: https://github.com/angular/angular/issues/15634
                timer(0).subscribe(() => {
                    this.handlingUnitValidation.handlingUnitValidationErrorsToast(this.claimDetail);
                    if (this.claimDetail.attachmentDirectory != null) {
                        this.getClaimAttachments(this.claimDetail.attachmentDirectory);
                    } else {
                        this.loadingService.stop();
                    }
                });
            } else {
                this.router.navigate(['/', 'claim']);
            }
        }));
    }

    getClaimAttachments(directory: string): void {
        this.subscription.add(this.fileManagerApi.getDirectorySummary(directory)
                .pipe(finalize(() => {
                            this.updateSendButton();
                        })
                )
                .subscribe(files => {
                    this.attachments = files;
                }, error => {
                    this.fileManagerOffline = true;
                    this.toast.error([
                        '<p>Orcan is not being able to provide the previously attached files list at the moment.</p>',
                        '<p><strong>Please use the refresh button to reattempt a communication with the server.</strong></p>',
                    ].join('<br>'), 'Error');
                }).add(() => {
                    this.loadingService.stop();
                }));
    }

    updateSendButton(): void {
        if (this.userService.hasPermissions([Permission.PARTNER_CLAIM_SEND])
                && this.claimDetail.status.code === 'DRAFT') {
            this.stickyHeaderService.registerSubmitAction({
                text: 'Send',
                click: (): void => {
                    this.claimSendHelper.sendClaim(this.claimDetail.number,
                            this.claimDetail.supplyingPackingPlant.code,
                            this.claimDetail.material.number,
                            () => {
                                this.router.navigate(['/', 'claim', this.claimDetail.number]);
                            });
                },
                id: 'sticky-header-send-button',
                class: 'btn-primary',
                dataCy: 'sticky-header-send-button',
                disabled: this.claimDetail.availableActions
                        && !this.claimDetail.availableActions.includes(AvailableActions.SUBMIT) || this.fileManagerOffline,
                tooltip: 'Send Claim'
            });
        }
    }

    refreshAttachments = (): void => {
        this.loadingService.start();
        if (this.claimDetail.attachmentDirectory != null) {
            this.fileManagerOffline = false;
            this.getClaimAttachments(this.claimDetail.attachmentDirectory);
        } else {
            this.loadingService.stop();
        }
    };

    deleteClaim(claimDetail: ClaimDetailModel): void {
        const confirmModalInstance: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent, {
            centered: true
        }).componentInstance;

        const confirmModal: ConfirmModal = {
            titleText: 'Delete',
            subtitleText: `Are you sure you want to delete draft Claim number ${claimDetail.number}?`,
            alertNotificationSeverity: AlertNotificationSeverity.ERROR,
            primaryButtonText: 'Delete',
            primaryButtonCustomizedClass: 'btn-primary-red',
            secondaryButtonText: 'Cancel',
            manualChecks: {
                title: `<strong>Warning:</strong> This action cannot be undone`,
                inputs: [
                    {
                        checked: false,
                        message: 'All header, position information and files attached will be deleted'
                    }
                ]
            }
        };
        confirmModalInstance.initModal(confirmModal).confirmEvent.subscribe(() => {
            this.loadingService.start();
            forkJoin([
                this.claimsApi.deleteClaim(claimDetail.number),
                this.folderDeleteService.deleteFolder(claimDetail.attachmentDirectory)
            ]).subscribe(() => {
                this.toastrService.success(`${claimDetail.number} <strong>deleted</strong> successfully`, 'Success');
                this.router.navigate(['/', 'claim']);
            }, error => {
                this.toastrService.error(`An error has occurred. ${error}`, 'Error');
            }).add(() => {
                /** Stop spinner loading*/
                this.loadingService.stop();
            });
        });
    }

    contextMenuSend(): void {
        this.claimSendHelper.sendClaim(this.claimDetail.number,
                this.claimDetail.supplyingPackingPlant.code,
                this.claimDetail.material.number,
                () => {
                    this.router.navigate(['/', 'claim', this.claimDetail.number]);
                });
    }

    canPerformAction(claimDetail: ClaimDetailModel, action: AvailableActions): boolean {
        return claimDetail.availableActions != null && claimDetail.availableActions.includes(action);
    }

    contextMenuEdit(): void {
        this.router.navigate(['/', 'claim', this.claimDetail.number, 'edit']);
    }

    mapClaimInformation = (claimInformation: ClaimDetailModel): ClaimInformationDataModel => ({
        partner: claimInformation.partner,
        email: claimInformation.email ? claimInformation.email : null,
        material: {
            number: claimInformation.material.number,
            description: claimInformation.material.description,
            salesUnit: claimInformation.material.salesUnit,
            supplyingPackingPlantCode: claimInformation.material.supplyingPackingPlantCode,
            active: claimInformation.material.active
        },
        modelType: claimInformation.modelType,
        lotNumber: claimInformation.lotNumber,
        modelCode: claimInformation.modelCode,
        mcdrNumber: claimInformation.mcdrNumber,
        supplyingPackingPlant: claimInformation.supplyingPackingPlant,
        claimCode: claimInformation.claimCode,
        subject: claimInformation.subject,
        spoNumber: claimInformation.spoNumber,
        description: claimInformation.description,
        sentDate: claimInformation.sentDate
    });

    ngOnDestroy(): void {
        this.stickyHeaderService.clearActions();
        this.subscription.unsubscribe();
    }

    getCssClass(): string {
        if (this.claimDetail.status.code === ClaimStatusEnum.DRAFT) {
            return 'claim-detail-header-claim-reference-number-draft mouse';
        } else if (this.claimDetail.referenceNumber && this.claimDetail.referenceNumber.trim().length > 0) {
            return 'claim-detail-header-claim-reference-number';
        } else {
            return 'claim-detail-header-claim-reference-number-draft';
        }
    }

    getReferenceNumberLabel(): string {
        if (this.claimDetail.status.code === ClaimStatusEnum.DRAFT) {
            return '';
        } else if (this.claimDetail.referenceNumber && this.claimDetail.referenceNumber.trim().length > 0) {
            return this.claimDetail.referenceNumber;
        } else {
            return 'In progress...';
        }
    }

    showRefLabel(): boolean {
        return this.claimDetail.status.code !== ClaimStatusEnum.DRAFT ||
                (this.claimDetail.referenceNumber != null && this.claimDetail.referenceNumber.trim().length > 0);
    }

    showReferenceNumberTooltip(): boolean {
        return this.claimDetail.status.code === ClaimStatusEnum.DRAFT;
    }

    showClaimProcessBar(): boolean {
        return this.statusSchemaView != null && !this.state.isLoading;
    }
}
